import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

interface PropsCard {
  children: ReactNode;
  className?: string;
}

/** Panel de Foco: superficie suave, esquinas de 23 px y sin borde. */
export function Card({ children, className = '' }: PropsCard) {
  return <div className={`rounded-panel bg-superficie p-5 ${className}`.trim()}>{children}</div>;
}

interface PropsSeccion {
  titulo: string;
  descripcion?: string;
  children: ReactNode;
  /** Texto corto a la derecha del título, como "2 de 3 comidas". */
  extra?: ReactNode;
}

export function Seccion({ titulo, descripcion, children, extra }: PropsSeccion) {
  return (
    <section className="space-y-3">
      <div>
        <div className="flex items-center justify-between gap-2.5">
          <h2 className="text-[17px] leading-snug tracking-[-0.25px] text-texto">{titulo}</h2>
          {extra !== undefined && (
            <span className="whitespace-nowrap text-[12px] text-texto-suave">{extra}</span>
          )}
        </div>
        {descripcion !== undefined && (
          <p className="mt-1 text-[13px] leading-relaxed text-texto-suave">{descripcion}</p>
        )}
      </div>
      {children}
    </section>
  );
}

interface PropsDato {
  etiqueta: string;
  valor: ReactNode;
  detalle?: string;
}

/** Renglón de etiqueta y valor, para resúmenes y ajustes. */
export function Dato({ etiqueta, valor, detalle }: PropsDato) {
  return (
    <div className="flex items-start justify-between gap-3.5 py-2.5">
      <div className="min-w-0">
        <dt className="text-[13px] text-texto-suave">{etiqueta}</dt>
        {detalle !== undefined && <p className="text-[12px] text-texto-suave">{detalle}</p>}
      </div>
      <dd className="shrink-0 text-right text-[13px] font-medium text-texto">{valor}</dd>
    </div>
  );
}

/** Ícono dentro de un cuadro suave, como el emblema de los paneles de Foco. */
export function Emblema({ icono: Icono }: { icono: LucideIcon }) {
  return (
    <span className="grid size-[43px] shrink-0 place-items-center rounded-2xl bg-superficie-suave text-marca">
      <Icono className="size-5" />
    </span>
  );
}

interface PropsVacio {
  titulo: string;
  descripcion?: string;
  icono?: LucideIcon;
  children?: ReactNode;
}

/**
 * Estado vacío explícito: una invitación breve en lugar de ceros o una
 * sección en blanco (Req. 24.3). Con el estilo de los paneles de Foco.
 */
export function EstadoVacio({ titulo, descripcion, icono, children }: PropsVacio) {
  return (
    <div className="rounded-panel bg-superficie p-5">
      {icono !== undefined && <Emblema icono={icono} />}
      <h3
        className={`text-[17px] leading-snug tracking-[-0.25px] text-texto ${icono !== undefined ? 'mt-3' : ''}`}
      >
        {titulo}
      </h3>
      {descripcion !== undefined && (
        <p className="mt-1.5 text-[13px] leading-relaxed text-texto-suave">{descripcion}</p>
      )}
      {children !== undefined && <div className="mt-5">{children}</div>}
    </div>
  );
}

/** Pastilla de Foco: una etiqueta corta como "Fase 4" o "Hoy". */
export function Pastilla({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-[5px] rounded-full bg-marca-suave px-2.5 py-[5px] text-[11px] leading-none text-marca">
      {children}
    </span>
  );
}
